import { useState, useEffect } from "react";
import {
  Container, Header, Table, Button, SpaceBetween, Box, Badge,
  Select, Toggle, Alert, Modal, FormField, Input,
} from "@cloudscape-design/components";
import { adminService } from "../../services/adminService";
import { useAuth } from "../../context/AuthContext";

export default function MenuManagement() {
  const [menuItems, setMenuItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pendingChanges, setPendingChanges] = useState([]);
  const { adminUser } = useAuth();
  const isSuper = adminUser?.admin_level === "super";

  // Create menu item modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", path: "", icon: "file", order: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, g] = await Promise.all([adminService.getMenuItems(), adminService.getGroups()]);
      setMenuItems(m);
      setGroups(g.map(gr => ({ value: gr.group_id || gr.name, label: gr.name, description: gr.description })));
    } catch (e) {
      setError("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedGroup) { setPermissions({}); setPendingChanges([]); return; }
    loadPermissions(selectedGroup.value);
  }, [selectedGroup]);

  const loadPermissions = async (groupId) => {
    try {
      const perms = await adminService.getGroupMenuPermissions(groupId);
      const map = {};
      perms.forEach(p => { map[p.menu_id] = p.can_view; });
      setPermissions(map);
      setPendingChanges([]);
    } catch {
      setError("Failed to load permissions.");
    }
  };

  const handleToggle = (menuId, menuName, newVal) => {
    setPermissions(p => ({ ...p, [menuId]: newVal }));
    setPendingChanges(prev => {
      const filtered = prev.filter(c => !c.includes(menuName));
      return [...filtered, `${menuName}: ${newVal ? "Enable" : "Disable"} access`];
    });
  };

  const savePermissions = async () => {
    if (!selectedGroup) return;
    setSaving(true);
    try {
      const allowed = Object.entries(permissions).filter(([, v]) => v).map(([k]) => k);
      await adminService.updateGroupMenuPermissions(selectedGroup.value, allowed);
      setSuccess(`Permissions saved for ${selectedGroup.label}`);
      setPendingChanges([]);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  const seedMenus = async () => {
    try {
      await adminService.seedMenus();
      setSuccess("Default menu items seeded.");
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to seed menus.");
    }
  };

  const openCreate = () => {
    setCreateForm({ name: "", path: "", icon: "file", order: String(menuItems.length + 1) });
    setCreateError("");
    setShowCreate(true);
  };

  const handleCreate = async () => {
    setCreateError("");
    if (!createForm.name.trim() || !createForm.path.trim()) {
      setCreateError("Name and path are required.");
      return;
    }
    if (!createForm.path.startsWith("/")) {
      setCreateError("Path must start with / (e.g. /boxer/my-page).");
      return;
    }
    setCreating(true);
    try {
      await adminService.createMenuItem(
        createForm.name.trim(),
        createForm.path.trim(),
        createForm.icon.trim() || "file",
        parseInt(createForm.order) || menuItems.length + 1,
      );
      setSuccess(`Menu item "${createForm.name}" created. You can now set group permissions below.`);
      setShowCreate(false);
      load();
    } catch (e) {
      setCreateError(e.response?.data?.detail || "Failed to create menu item.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete menu item "${item.name}"? This will remove it from all group permissions.`)) return;
    try {
      await adminService.deleteMenuItem(item.menu_id);
      setSuccess(`Menu item "${item.name}" deleted.`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete menu item.");
    }
  };

  const cols = [
    {
      id: "name", header: "Menu Item",
      cell: i => <SpaceBetween size="xs" direction="vertical">
        <Box fontWeight="bold">{i.name}</Box>
        <Box fontSize="body-s" color="text-status-subdued">{i.path}</Box>
      </SpaceBetween>,
    },
    { id: "icon", header: "Icon", cell: i => <Badge color="blue">{i.icon}</Badge>, width: 100 },
    { id: "order", header: "Order", cell: i => i.order, width: 80 },
    {
      id: "status", header: "Status",
      cell: i => <Badge color={i.is_active ? "green" : "red"}>{i.is_active ? "Active" : "Inactive"}</Badge>,
      width: 100,
    },
    {
      id: "permission",
      header: selectedGroup ? `${selectedGroup.label} Access` : "Group Access",
      cell: i => selectedGroup
        ? <Toggle checked={!!permissions[i.menu_id]} onChange={({ detail }) => handleToggle(i.menu_id, i.name, detail.checked)} disabled={saving} />
        : <Badge color={i.visible_to_groups?.length > 0 ? "green" : "grey"}>{i.visible_to_groups?.length || 0} group{i.visible_to_groups?.length !== 1 ? "s" : ""}</Badge>,
      width: 160,
    },
    ...(isSuper ? [{
      id: "actions", header: "Actions", width: 90,
      cell: i => (
        <Button size="small" onClick={() => handleDelete(i)}>❌</Button>
      ),
    }] : []),
  ];

  if (loading && menuItems.length === 0) {
    return (
      <Container header={<Header>Menu Management</Header>}>
        <Box textAlign="center" padding="l" color="text-body-secondary">Loading menu data...</Box>
      </Container>
    );
  }

  return (
    <Container header={
      <Header variant="h2" description="Control which menu items each group can see"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            {selectedGroup && (
              <>
                <Button onClick={() => loadPermissions(selectedGroup.value)}>Refresh Permissions</Button>
                <Button variant="primary" onClick={savePermissions} loading={saving}>Save Permissions</Button>
              </>
            )}
            {isSuper && <Button onClick={openCreate}>Add Menu Item</Button>}
          </SpaceBetween>
        }>
        Menu Management
      </Header>
    }>
      <SpaceBetween size="l">
        {error && <Alert type="error" dismissible onDismiss={() => setError("")}>{error}</Alert>}
        {success && <Alert type="success" dismissible onDismiss={() => setSuccess("")}>{success}</Alert>}

        <SpaceBetween direction="horizontal" size="s">
          <Select
            selectedOption={selectedGroup}
            onChange={({ detail }) => setSelectedGroup(detail.selectedOption)}
            options={groups}
            placeholder="Select a group to manage permissions"
            filteringType="auto"
            disabled={saving}
          />
          {menuItems.length === 0 && (
            <Button onClick={seedMenus}>Seed Default Menu Items</Button>
          )}
        </SpaceBetween>

        {!selectedGroup && (
          <Alert type="info">Select a group above to toggle menu permissions. The table below shows all menu items and their current group access counts.</Alert>
        )}

        {pendingChanges.length > 0 && (
          <Alert type="warning">
            <SpaceBetween size="xs">
              <Box fontWeight="bold">Unsaved changes:</Box>
              {pendingChanges.slice(0, 5).map((c, i) => <Box key={i} fontSize="body-s">• {c}</Box>)}
              {pendingChanges.length > 5 && <Box fontSize="body-s">• ... and {pendingChanges.length - 5} more</Box>}
            </SpaceBetween>
          </Alert>
        )}

        <Table
          columnDefinitions={cols}
          items={menuItems}
          loading={saving}
          loadingText="Saving permissions..."
          header={<Header counter={`(${menuItems.length})`} actions={<Button onClick={load}>Refresh</Button>}>Menu Items</Header>}
          empty={<Box textAlign="center" color="text-body-secondary">No menu items. <Button onClick={seedMenus}>Seed defaults</Button></Box>}
        />

        <Modal
          visible={showCreate}
          onDismiss={() => setShowCreate(false)}
          header="Add Menu Item"
          footer={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleCreate} loading={creating}>Create</Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="m">
            {createError && <Alert type="error">{createError}</Alert>}
            <Alert type="info">
              The path must match a route that has already been added to the application code by a developer.
              After creating, go to the group permissions toggles to grant access.
            </Alert>
            <FormField label="Page Name" description="Displayed in the sidebar and home screen">
              <Input
                value={createForm.name}
                onChange={({ detail }) => setCreateForm(f => ({ ...f, name: detail.value }))}
                placeholder="e.g. Inventory Report"
              />
            </FormField>
            <FormField label="Route Path" description="Must start with / and match the app route exactly">
              <Input
                value={createForm.path}
                onChange={({ detail }) => setCreateForm(f => ({ ...f, path: detail.value }))}
                placeholder="e.g. /boxer/inventory-report"
              />
            </FormField>
            <FormField label="Icon" description="Cloudscape icon name (optional)">
              <Input
                value={createForm.icon}
                onChange={({ detail }) => setCreateForm(f => ({ ...f, icon: detail.value }))}
                placeholder="e.g. file, settings, search"
              />
            </FormField>
            <FormField label="Display Order" description="Lower numbers appear first in the menu">
              <Input
                type="number"
                value={createForm.order}
                onChange={({ detail }) => setCreateForm(f => ({ ...f, order: detail.value }))}
                placeholder="e.g. 5"
              />
            </FormField>
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </Container>
  );
}
